// ═══════════════════════════════════════════════════════
// Auth utilities — JWT decode + TMS passenger-auth proxy helpers
// (passenger accounts live in TMS itself, not 9nerd-OAuth)
// ═══════════════════════════════════════════════════════

import { NextResponse } from "next/server";
import { ACCESS_COOKIE, REFRESH_COOKIE } from "./cookies";

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return Date.now() / 1000 > payload.exp - bufferSeconds;
}

// TMS API base (server-side route handlers)
export function getTmsApiBase(): string {
  return (
    process.env.API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    "http://localhost:17003"
  );
}

/** Extract a readable error message from a NestJS error body. */
export function apiErrorMessage(data: unknown, fallback: string): string {
  const body = data as { message?: string | string[]; error?: string } | null;
  if (!body) return fallback;
  if (Array.isArray(body.message)) return body.message[0] ?? fallback;
  return body.message || body.error || fallback;
}

/** Set the session cookie pair on a route response. */
export function setSessionCookies(
  response: NextResponse,
  tokens: { access_token: string; refresh_token: string; expires_in?: number },
): void {
  const isProduction = process.env.NODE_ENV === "production";

  response.cookies.set(ACCESS_COOKIE, tokens.access_token, {
    httpOnly: false, // JS decodes it for user info
    secure: isProduction,
    sameSite: "lax",
    path: "/",
    maxAge: tokens.expires_in || 900,
  });

  response.cookies.set(REFRESH_COOKIE, tokens.refresh_token, {
    httpOnly: true,
    secure: isProduction,
    sameSite: "lax",
    path: "/",
    maxAge: 30 * 24 * 60 * 60, // 30 days
  });
}

export function clearSessionCookies(response: NextResponse): void {
  response.cookies.delete(ACCESS_COOKIE);
  response.cookies.delete(REFRESH_COOKIE);
}
